import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  message,
  Collapse,
  Card,
  Typography,
  InputNumber,
  List,
  Tag as AntdTag,
  Divider,
} from 'antd';
import { RobotOutlined, CheckOutlined, StarOutlined } from '@ant-design/icons';
import { SourceChapter, OutlineReviewVersion, ContentReviewVersion } from '../../types';
import { sourceStoriesApi, CreateSourceChapterRequest, UpdateSourceChapterRequest } from '../../services/api';
import { useAsyncOperation } from '../../hooks';

const { TextArea } = Input;
const { Text } = Typography;

interface SourceChapterModalProps {
  storyId: string;
  visible: boolean;
  editingChapter: SourceChapter | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const SourceChapterModal: React.FC<SourceChapterModalProps> = ({
  storyId,
  visible,
  editingChapter,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();
  
  // Phase 2.5: Outline Review Loop states
  const [outlineVersions, setOutlineVersions] = useState<OutlineReviewVersion[]>([]);
  const [scoreThreshold, setScoreThreshold] = useState<number>(80);
  const [maxReviewIterations, setMaxReviewIterations] = useState<number>(3);
  const [isReviewLooping, setIsReviewLooping] = useState<boolean>(false);

  // Phase 3.5: Content Review Loop states
  const [contentVersions, setContentVersions] = useState<ContentReviewVersion[]>([]);
  const [isContentReviewLooping, setIsContentReviewLooping] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      if (editingChapter) {
        form.setFieldsValue({
          name: editingChapter.name,
          link: editingChapter.link,
          instruction: editingChapter.instruction,
          outline: editingChapter.outline,
          content: editingChapter.content,
        });
        setOutlineVersions([]);
        setContentVersions([]);
      } else {
        form.resetFields();
        setOutlineVersions([]);
        setContentVersions([]);
      }
    }
  }, [visible, editingChapter, form]);

  const handleSubmit = async (values: any) => {
    const operation = editingChapter
      ? () => sourceStoriesApi.updateSourceChapter(
          storyId,
          editingChapter.id,
          values as UpdateSourceChapterRequest
        )
      : () => sourceStoriesApi.createSourceChapter(storyId, values as CreateSourceChapterRequest);

    const result = await executeAsync(operation, {
      successMessage: editingChapter ? 'Chapter updated successfully!' : 'Chapter created successfully!',
    });

    if (result) {
      onSuccess();
    }
  };

  const runOutlineReviewLoop = async (initialOutline: string, instruction: string) => {
    if (!editingChapter) {
        message.warning('Chapter must be saved before review loop. Saving current draft...');
        return;
    }

    setIsReviewLooping(true);
    setOutlineVersions([]);
    
    let currentOutline = initialOutline;
    let versions: OutlineReviewVersion[] = [];

    try {
        for (let i = 0; i < maxReviewIterations; i++) {
            // 1. Review
            const reviewResponse = await sourceStoriesApi.reviewChapterOutline(
                storyId, 
                editingChapter.id, 
                instruction, 
                currentOutline
            );
            
            const audienceReviews = reviewResponse.data;
            const avgScore = audienceReviews.reduce((sum, r) => sum + r.score, 0) / audienceReviews.length;
            
            const newVersion: OutlineReviewVersion = {
                roundNumber: i + 1,
                outline: currentOutline,
                averageScore: parseFloat(avgScore.toFixed(2)),
                audienceReviews
            };
            
            versions = [...versions, newVersion];
            setOutlineVersions(versions);

            // Check threshold
            if (avgScore >= scoreThreshold) {
                message.success(`Threshold reached with score ${newVersion.averageScore}! Stopping loop.`);
                break;
            }

            // 2. Regen (if not last round)
            if (i < maxReviewIterations - 1) {
                const allFeedbacks = audienceReviews.flatMap(r => r.feedbacks);
                const regenResponse = await sourceStoriesApi.regenChapterOutline(
                    storyId,
                    editingChapter.id,
                    instruction,
                    currentOutline,
                    allFeedbacks
                );
                currentOutline = regenResponse.data.outline;
            } else {
                message.info('Reached maximum review iterations.');
            }
        }
    } catch (error) {
        console.error('Error in outline review loop:', error);
        message.error('Failed to complete outline review loop');
    } finally {
        setIsReviewLooping(false);
    }
  };

  const handleGenerateOutline = async () => {
    const instruction = form.getFieldValue('instruction');
    if (!instruction) {
      message.warning('Please enter instruction first');
      return;
    }

    await executeAsync(
      async () => {
        const response = await sourceStoriesApi.generateChapterOutline(storyId, instruction, editingChapter?.id);
        const outline = response.data.outline;
        form.setFieldsValue({ outline });
        
        if (editingChapter) {
            runOutlineReviewLoop(outline, instruction);
        } else {
            message.info('Outline generated. Please save chapter to start automatic AI review loop.');
        }
      },
      { successMessage: 'Initial outline generated!' }
    );
  };

  const handleApplyOutlineVersion = (version: OutlineReviewVersion) => {
    form.setFieldsValue({ outline: version.outline });
    message.success(`Applied Round ${version.roundNumber} outline version.`);
  };

  const runContentReviewLoop = async (initialContent: string, instruction: string, outline: string) => {
    if (!editingChapter) {
        message.warning('Chapter must be saved before review loop. Saving current draft...');
        return;
    }

    setIsContentReviewLooping(true);
    setContentVersions([]);
    
    let currentContent = initialContent;
    let versions: ContentReviewVersion[] = [];

    try {
        for (let i = 0; i < maxReviewIterations; i++) {
            // 1. Review
            const reviewResponse = await sourceStoriesApi.reviewChapter(
                storyId, 
                editingChapter.id, 
                instruction, 
                outline,
                currentContent
            );
            
            const audienceReviews = reviewResponse.data;
            const avgScore = audienceReviews.reduce((sum, r) => sum + r.score, 0) / audienceReviews.length;
            
            const newVersion: ContentReviewVersion = {
                roundNumber: i + 1,
                content: currentContent,
                averageScore: parseFloat(avgScore.toFixed(2)),
                audienceReviews
            };
            
            versions = [...versions, newVersion];
            setContentVersions(versions);

            // Check threshold
            if (avgScore >= scoreThreshold) {
                message.success(`Threshold reached with score ${newVersion.averageScore}! Stopping loop.`);
                break;
            }

            // 2. Regen (if not last round)
            if (i < maxReviewIterations - 1) {
                const allFeedbacks = audienceReviews.flatMap(r => r.feedbacks);
                const regenResponse = await sourceStoriesApi.rewriteChapter(
                    storyId,
                    editingChapter.id,
                    instruction,
                    outline,
                    currentContent,
                    allFeedbacks
                );
                currentContent = regenResponse.data.content;
            } else {
                message.info('Reached maximum review iterations.');
            }
        }
    } catch (error) {
        console.error('Error in content review loop:', error);
        message.error('Failed to complete content review loop');
    } finally {
        setIsContentReviewLooping(false);
    }
  };

  const handleGenerateContent = async () => {
    const instruction = form.getFieldValue('instruction');
    const outline = form.getFieldValue('outline');
    
    if (!instruction || !outline) {
      message.warning('Please enter instruction and outline first');
      return;
    }

    await executeAsync(
      async () => {
        const response = await sourceStoriesApi.generateChapterContent(storyId, instruction, outline);
        const content = response.data.content;
        form.setFieldsValue({ content });

        if (editingChapter) {
            runContentReviewLoop(content, instruction, outline);
        } else {
            message.info('Content generated. Please save chapter to start automatic AI review loop.');
        }
      },
      { successMessage: 'Initial content generated!' }
    );
  };

  const handleApplyContentVersion = (version: ContentReviewVersion) => {
    form.setFieldsValue({ content: version.content });
    message.success(`Applied Round ${version.roundNumber} content version.`);
  };

  return (
    <Modal
      title={editingChapter ? 'Edit Chapter' : 'Add New Chapter'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      centered
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label="Chapter Name"
        >
          <Input placeholder="Enter chapter name (optional)..." />
        </Form.Item>

        <Form.Item
          name="link"
          label="Link"
        >
          <Input placeholder="Enter original chapter link (optional)..." />
        </Form.Item>

        <Form.Item
          name="instruction"
          label={
            <Space split={<Divider type="vertical" />}>
              <Text strong>Instruction (AI Guide)</Text>
              <Space>
                <Text type="secondary">Threshold:</Text>
                <InputNumber 
                  size="small" 
                  min={0} 
                  max={100} 
                  step={1} 
                  value={scoreThreshold} 
                  onChange={(v) => setScoreThreshold(v || 80)}
                  style={{ width: 60 }}
                />
              </Space>
              <Space>
                <Text type="secondary">Max Rounds:</Text>
                <InputNumber 
                  size="small" 
                  min={1} 
                  max={5} 
                  value={maxReviewIterations} 
                  onChange={(v) => setMaxReviewIterations(v || 3)}
                  style={{ width: 50 }}
                />
              </Space>
              <Button 
                type="link" 
                size="small" 
                icon={<RobotOutlined />} 
                onClick={handleGenerateOutline}
                loading={asyncLoading || isReviewLooping}
              >
                AI Generate & Auto Review
              </Button>
            </Space>
          }
        >
          <TextArea
            rows={4}
            placeholder="Enter instruction for AI to generate outline and content..."
          />
        </Form.Item>

        <Form.Item
          name="outline"
          label={
            <Space>
              <Text strong>Outline</Text>
              <Button 
                type="link" 
                size="small" 
                icon={<RobotOutlined />} 
                onClick={handleGenerateContent}
                loading={asyncLoading}
                disabled={isReviewLooping}
              >
                AI Generate Content
              </Button>
            </Space>
          }
        >
          <TextArea
            rows={8}
            placeholder="Chapter outline..."
          />
        </Form.Item>

        {outlineVersions.length > 0 && (
          <Card 
            size="small" 
            title={
              <Space>
                <StarOutlined />
                <span>Outline Versions (AI Review Loop)</span>
                {isReviewLooping && <Text type="secondary">(Looping... Round {outlineVersions.length})</Text>}
              </Space>
            }
            style={{ marginBottom: 24, background: '#f0f5ff' }}
          >
            <List
              itemLayout="vertical"
              dataSource={outlineVersions}
              renderItem={(version) => {
                const isBest = version.averageScore === Math.max(...outlineVersions.map(v => v.averageScore));
                return (
                  <List.Item
                    extra={
                      <Button 
                        type={isBest ? "primary" : "default"}
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleApplyOutlineVersion(version)}
                      >
                        Apply
                      </Button>
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <AntdTag color="blue">Round {version.roundNumber}</AntdTag>
                          <Text strong>Avg Score: {version.averageScore}</Text>
                          {isBest && <AntdTag color="gold">Best Score</AntdTag>}
                        </Space>
                      }
                      description={
                        <Collapse ghost>
                          <Collapse.Panel header="View Audience Reviews" key="reviews">
                            {version.audienceReviews.map(r => (
                              <div key={r.audienceCode} style={{ marginBottom: 8 }}>
                                <Space>
                                  <Text type="secondary">{r.audienceName}:</Text>
                                  <Text strong>{r.score}</Text>
                                </Space>
                                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                  {r.feedbacks.map((f, idx) => (
                                    <li key={idx}><Text style={{ fontSize: '12px' }}>{f}</Text></li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </Collapse.Panel>
                        </Collapse>
                      }
                    />
                    <div style={{ 
                      maxHeight: 100, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'pre-wrap',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 4,
                      fontSize: '12px',
                      border: '1px solid #d9d9d9'
                    }}>
                      {version.outline}
                    </div>
                  </List.Item>
                );
              }}
            />
          </Card>
        )}

        <Form.Item
          name="content"
          label={
            <Space>
              <Text strong>Content</Text>
              <Button 
                type="link" 
                size="small" 
                icon={<RobotOutlined />} 
                onClick={handleGenerateContent}
                loading={asyncLoading || isContentReviewLooping}
                disabled={isReviewLooping}
              >
                AI Generate & Auto Review
              </Button>
            </Space>
          }
        >
          <TextArea
            rows={15}
            placeholder="Chapter content..."
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        {contentVersions.length > 0 && (
          <Card 
            size="small" 
            title={
              <Space>
                <StarOutlined />
                <span>Content Versions (AI Review Loop)</span>
                {isContentReviewLooping && <Text type="secondary">(Looping... Round {contentVersions.length})</Text>}
              </Space>
            }
            style={{ marginBottom: 24, background: '#f6ffed', borderColor: '#b7eb8f' }}
          >
            <List
              itemLayout="vertical"
              dataSource={contentVersions}
              renderItem={(version) => {
                const isBest = version.averageScore === Math.max(...contentVersions.map(v => v.averageScore));
                return (
                  <List.Item
                    extra={
                      <Button 
                        type={isBest ? "primary" : "default"}
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleApplyContentVersion(version)}
                      >
                        Apply
                      </Button>
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <AntdTag color="green">Round {version.roundNumber}</AntdTag>
                          <Text strong>Avg Score: {version.averageScore}</Text>
                          {isBest && <AntdTag color="gold">Best Score</AntdTag>}
                        </Space>
                      }
                      description={
                        <Collapse ghost>
                          <Collapse.Panel header="View Audience Reviews" key="reviews">
                            {version.audienceReviews.map(r => (
                              <div key={r.audienceCode} style={{ marginBottom: 8 }}>
                                <Space>
                                  <Text type="secondary">{r.audienceName}:</Text>
                                  <Text strong>{r.score}</Text>
                                </Space>
                                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                  {r.feedbacks.map((f, idx) => (
                                    <li key={idx}><Text style={{ fontSize: '12px' }}>{f}</Text></li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </Collapse.Panel>
                        </Collapse>
                      }
                    />
                    <div style={{ 
                      maxHeight: 120, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'pre-wrap',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 4,
                      fontSize: '12px',
                      border: '1px solid #d9d9d9',
                      fontFamily: 'monospace'
                    }}>
                      {version.content}
                    </div>
                  </List.Item>
                );
              }}
            />
          </Card>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={asyncLoading}
            >
              {editingChapter ? 'Update' : 'Create'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SourceChapterModal;
